import { ArrowBack, Lock as LockIcon } from "@mui/icons-material";
import {
  TextField,
  Button,
  Typography,
  Box,
  MenuItem,
  Collapse,
  Alert,
  CircularProgress,
  Link,
  IconButton,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import SocialAuthButtons from "./SocialAuthButtons";
import { FormCard } from "../../../components/ui";
import { LEGAL_LINKS, legalLinkProps } from "../../../config/legalLinks";
import { useOAuthProviders } from "../../../hooks/useOAuthProviders";
import { getAllLanguagesInfo } from "../../../utils/languages";

export interface AuthFormProps {
  email: string;
  firstName: string;
  lastName: string;
  nativeLanguage: string;
  targetLanguage: string;
  isNewUser: boolean;
  registrationEnabled: boolean;
  registrationStep: 1 | 2;
  promoCode: string;
  hasActivePromoCodes: boolean;
  password: string;
  registerPassword: string;
  registerConfirmPassword: string;
  loading: boolean;
  error: string | null;
  onEmailChange: (v: string) => void;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onNativeLanguageChange: (v: string) => void;
  onTargetLanguageChange: (v: string) => void;
  onNewUserToggle: () => void;
  onPromoCodeChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onRegisterPasswordChange: (v: string) => void;
  onRegisterConfirmPasswordChange: (v: string) => void;
  onRegister: (e: React.FormEvent) => void;
  onPasswordLogin: (e: React.FormEvent) => void;
  onNextRegistrationStep: () => void;
  onBackRegistrationStep: () => void;
  onErrorDismiss: () => void;
}

const LANGUAGES = getAllLanguagesInfo().map(
  (language): { code: string; name: string } => ({
    code: language.code,
    name: language.nativeName,
  }),
);

const AuthForm: React.FC<AuthFormProps> = ({
  email,
  firstName,
  lastName,
  nativeLanguage,
  targetLanguage,
  isNewUser,
  registrationEnabled,
  registrationStep,
  promoCode,
  hasActivePromoCodes,
  password,
  registerPassword,
  registerConfirmPassword,
  loading,
  error,
  onEmailChange,
  onFirstNameChange,
  onLastNameChange,
  onNativeLanguageChange,
  onTargetLanguageChange,
  onNewUserToggle,
  onPromoCodeChange,
  onPasswordChange,
  onRegisterPasswordChange,
  onRegisterConfirmPasswordChange,
  onRegister,
  onPasswordLogin,
  onNextRegistrationStep,
  onBackRegistrationStep,
  onErrorDismiss,
}) => {
  const { t } = useTranslation("auth");
  const { isProviderEnabled, isProviderConfigured } = useOAuthProviders();
  const hasSocialProviders = (
    ["google", "microsoft", "apple", "facebook"] as const
  ).some(
    (provider): boolean =>
      isProviderEnabled(provider) && isProviderConfigured(provider),
  );

  const getTitle = (): string => {
    if (!isNewUser) return t("login.welcomeBack");
    if (registrationStep === 1)
      return t("register.chooseLanguages", {
        defaultValue: "Choose your languages",
      });
    return t("register.joinLingoShade");
  };

  const getSubtitle = (): string => {
    if (!isNewUser)
      return registrationEnabled
        ? t("otp.signInOrSignUpMessage")
        : t("password.signIn");
    if (registrationStep === 1)
      return t("register.languagesSubtitle", {
        defaultValue:
          "Select the language you want to learn and your native language",
      });
    return t("register.credentialsSubtitle", {
      defaultValue: "Create your account",
    });
  };

  return (
    <FormCard title={getTitle()} subtitle={getSubtitle()} maxWidth="sm">
      <Collapse in={!!error}>
        <Alert severity="error" onClose={onErrorDismiss} sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Collapse>

      {/* Registration step 1: language selection */}
      {isNewUser && registrationStep === 1 && (
        <Box>
          <TextField
            fullWidth
            select
            label={t("otp.targetLanguage")}
            value={targetLanguage}
            onChange={(e) => onTargetLanguageChange(e.target.value)}
            disabled={loading}
            required
            helperText={t("otp.selectLanguageToLearn")}
            sx={{ mb: 3 }}
          >
            {LANGUAGES.map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            select
            label={t("otp.nativeLanguage")}
            value={nativeLanguage}
            onChange={(e) => onNativeLanguageChange(e.target.value)}
            disabled={loading}
            helperText={t("otp.changeIfNeeded")}
            sx={{ mb: 3 }}
          >
            {LANGUAGES.map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            fullWidth
            variant="contained"
            onClick={onNextRegistrationStep}
            disabled={loading || !targetLanguage}
            sx={{ mb: 2 }}
          >
            {t("register.continue", { defaultValue: "Continue" })}
          </Button>
          <Box sx={{ textAlign: "center" }}>
            <Button size="small" onClick={onNewUserToggle} disabled={loading}>
              {t("otp.alreadyHaveAccount")}
            </Button>
          </Box>
        </Box>
      )}

      {/* Registration step 2: credentials */}
      {isNewUser && registrationStep === 2 && (
        <Box component="form" onSubmit={onRegister}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
            <IconButton
              aria-label={t("register.backToLanguages")}
              onClick={onBackRegistrationStep}
              disabled={loading}
              size="small"
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              {t("register.backToLanguages", {
                defaultValue: "Back to language selection",
              })}
            </Typography>
          </Box>
          <TextField
            fullWidth
            label={t("otp.email")}
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={loading}
            required
            sx={{ mb: 2 }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              fullWidth
              label={t("otp.firstName")}
              value={firstName}
              onChange={(e) => onFirstNameChange(e.target.value)}
              disabled={loading}
            />
            <TextField
              fullWidth
              label={t("otp.lastName")}
              value={lastName}
              onChange={(e) => onLastNameChange(e.target.value)}
              disabled={loading}
            />
          </Box>
          <TextField
            fullWidth
            label={t("register.password")}
            type="password"
            value={registerPassword}
            onChange={(e) => onRegisterPasswordChange(e.target.value)}
            disabled={loading}
            required
            helperText={t("register.passwordHelper")}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t("register.confirmPassword")}
            type="password"
            value={registerConfirmPassword}
            onChange={(e) => onRegisterConfirmPasswordChange(e.target.value)}
            disabled={loading}
            required
            error={
              registerConfirmPassword.length > 0 &&
              registerConfirmPassword !== registerPassword
            }
            helperText={
              registerConfirmPassword.length > 0 &&
              registerConfirmPassword !== registerPassword
                ? t("register.passwordMismatch")
                : undefined
            }
            sx={{ mb: 2 }}
          />
          {hasActivePromoCodes && (
            <TextField
              fullWidth
              label={t("otp.promoCode")}
              value={promoCode}
              onChange={(e) => onPromoCodeChange(e.target.value.toUpperCase())}
              disabled={loading}
              sx={{ mb: 2 }}
              helperText={t("otp.promoCodeHelper")}
              inputProps={{ maxLength: 50 }}
            />
          )}
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={
              loading || !email || !registerPassword || !registerConfirmPassword
            }
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{ mb: 2 }}
          >
            {loading
              ? t("register.creatingAccount")
              : t("register.createAccount")}
          </Button>
        </Box>
      )}

      {/* Login form */}
      {!isNewUser && (
        <Box component="form" onSubmit={onPasswordLogin}>
          <TextField
            fullWidth
            label={t("otp.email")}
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={loading}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t("login.password")}
            type="password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            disabled={loading}
            required
            sx={{ mb: 1 }}
          />
          <Box sx={{ textAlign: "right", mb: 2 }}>
            <Link
              component={RouterLink}
              to="/forgot-password"
              variant="body2"
              underline="hover"
            >
              {t("password.forgotPassword")}
            </Link>
          </Box>
          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={loading || !email || !password}
            startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
            sx={{ mb: 2 }}
          >
            {loading ? t("password.signingIn") : t("password.signIn")}
          </Button>
          {registrationEnabled && (
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Button size="small" onClick={onNewUserToggle} disabled={loading}>
                {t("otp.newUser")}
              </Button>
            </Box>
          )}
          {hasSocialProviders && (
            <>
              <Box sx={{ display: "flex", alignItems: "center", my: 2 }}>
                <Box
                  sx={{
                    flexGrow: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ px: 2, color: "text.secondary" }}
                >
                  {t("otp.orContinueWith")}
                </Typography>
                <Box
                  sx={{
                    flexGrow: 1,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                />
              </Box>
              <SocialAuthButtons mode="login" showDivider={false} />
            </>
          )}
        </Box>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 3, display: "block", textAlign: "center" }}
      >
        {t("otp.byUsingYouAgree")}{" "}
        <Box
          component="a"
          href={LEGAL_LINKS.TERMS_OF_SERVICE}
          {...legalLinkProps}
          sx={{ textDecoration: "underline", cursor: "pointer" }}
        >
          {t("otp.termsOfService")}
        </Box>
      </Typography>
    </FormCard>
  );
};

export default AuthForm;
